import {createDomainEvent, DomainEvent} from './domain-event'
import {BasketData} from '@/src/entities/basket'

/**
 * Basket Domain Events
 *
 * Events related to basket/cart operations
 */

// Event Types (constants for type safety)
export const BasketEventTypes = {
  ProductAdded: 'basket.product_added',
  ProductRemoved: 'basket.product_removed',
  QuantityChanged: 'basket.quantity_changed',
  CertificateAdded: 'basket.certificate_added',
  BasketCleared: 'basket.cleared',
  BasketSynced: 'basket.synced',
  BasketProcessStart: 'basket.process',
  BasketProcessFinish: 'basket.process.finish',
  OnUpdateBasket: 'basket.updated'
} as const

export interface BasketUpdatedPayload {
  basket: BasketData
}

export interface ProductAddedPayload {
  productId: Id
  productName: string
  imageUrl: string
  productUrl: string
  price: number
  variantId?: Id
  quantity: number
}

export interface ProductRemovedPayload {
  basketItemId: string
  productId: Id
  variantId?: Id
  productName?: string
}

export interface QuantityChangedPayload {
  basketItemId: string
  productId: Id
  previousQuantity: number
  newQuantity: number
}

export interface CertificateAddedPayload {
  productId: Id
  amount: number
}

export interface BasketSyncedPayload {
  itemCount: number
  totalAmount: number
}

export interface BasketProcessPayload {
  eventId: string
}
export interface BasketProcessFinishPayload {
  eventId: string
}

export type BasketUpdatedEvent = DomainEvent<BasketUpdatedPayload>
export type ProductAddedToBasketEvent = DomainEvent<ProductAddedPayload>
export type ProductRemovedFromBasketEvent = DomainEvent<ProductRemovedPayload>
export type BasketQuantityChangedEvent = DomainEvent<QuantityChangedPayload>
export type CertificateAddedToBasketEvent = DomainEvent<CertificateAddedPayload>
export type BasketSyncedEvent = DomainEvent<BasketSyncedPayload>
export type BasketProcessEvent = DomainEvent<BasketProcessPayload>
export type BasketClearedEvent = DomainEvent

// Event Factories
export const BasketEvents = {
  basketUpdated(payload: BasketUpdatedPayload): BasketUpdatedEvent {
    return createDomainEvent(BasketEventTypes.OnUpdateBasket, payload)
  },
  startProcess(payload: BasketProcessPayload): BasketProcessEvent {
    return createDomainEvent(BasketEventTypes.BasketProcessStart, payload)
  },
  finishProcess(payload: BasketProcessPayload): BasketProcessEvent {
    return createDomainEvent(BasketEventTypes.BasketProcessFinish, payload)
  },

  productAdded(payload: ProductAddedPayload, basketId?: string): ProductAddedToBasketEvent {
    return createDomainEvent(BasketEventTypes.ProductAdded, payload, basketId)
  },

  productRemoved(payload: ProductRemovedPayload, basketId?: string): ProductRemovedFromBasketEvent {
    return createDomainEvent(BasketEventTypes.ProductRemoved, payload, basketId)
  },

  cleared(basketId?: string): BasketClearedEvent {
    return createDomainEvent(BasketEventTypes.BasketCleared, undefined, basketId)
  },

  quantityChanged(payload: QuantityChangedPayload, basketId?: string): BasketQuantityChangedEvent {
    return createDomainEvent(BasketEventTypes.QuantityChanged, payload, basketId)
  },

  certificateAdded(
    payload: CertificateAddedPayload,
    basketId?: string,
  ): CertificateAddedToBasketEvent {
    return createDomainEvent(BasketEventTypes.CertificateAdded, payload, basketId)
  },

  synced(payload: BasketSyncedPayload, basketId?: string): BasketSyncedEvent {
    return createDomainEvent(BasketEventTypes.BasketSynced, payload, basketId)
  },
}
